import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import http from '../../services/http';
import { Toast } from 'primereact/toast';
import { InputMask } from "primereact/inputmask";


export default function VerifyCode() {

    const toast = useRef(null);
    const navigate = useNavigate();
    const { uid } = useParams();
    
    const [timeLeft, setTimeLeft] = useState(60);
    const [canResend, setCanResend] = useState(false);

    const formik = useFormik({
        initialValues: {
            code: '',
        },
        validationSchema: Yup.object({
            code: Yup.string()
                .length(6, 'Código deve ter 6 dígitos')
                .matches(/^\d{6}$/, 'Código deve conter apenas números')
                .required('Código é obrigatório'),
        }),
        onSubmit: values => {

            var data = {
                code: values.code,
                uid: uid
            }

            http.post('password/reset/verify/', data).then( (response) => {

                if (response.data.status === 'success') {
                    toast.current.show({ 
                        severity: 'success', 
                        summary: 'Sucesso', 
                        detail: 'Código verificado com sucesso!', 
                        life: 4000 
                    });

                    setTimeout(() => {
                        navigate(`/password/change/${response.data.uid}/${response.data.token}`);
                    }, 4000)
                }

                if (response.data.status === 'error') {
                    toast.current.show({ severity: 'warn', summary: 'Ops', detail: JSON.stringify(response.data.message), life: 4000 });
                }

                formik.setSubmitting(false);


            }).catch((error) => {
                toast.current.show({ severity: 'warn', summary: 'Ops', detail: JSON.stringify(error.response.data), life: 4000 });
                formik.setSubmitting(false);
            })

        }
    });

    const handleResendCode = async () => {
        if (!canResend) return;
        
        try {
            await http.post('password/reset/resend/', {
                uid: uid
            });

            setCanResend(false);
            setTimeLeft(60);
            
            toast.current?.show({ 
                severity: 'success', 
                summary: 'Sucesso', 
                detail: 'Novo código enviado com sucesso!', 
                life: 4000 
            });
        } catch (error) {
            toast.current?.show({ 
                severity: 'warn', 
                summary: 'Ops', 
                detail: 'Erro ao reenviar código', 
                life: 4000 
            });
        }
    };

    // Timer para reenvio de código
    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [timeLeft]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const numbersOnly = pastedText.replace(/\D/g, '').slice(0, 6);
        
        if (numbersOnly.length === 6) {
            formik.setFieldValue('code', numbersOnly);
        }
    };

    return (
        <div className="row mt-5">
            <Toast ref={toast} position="top-right" />

            <div className="col-md-6 offset-md-3">
                <div className="divBorder formMarginBottom">
                    <div className="p-4">
                        <div className="text-center mb-4">
                            <h1 className="mb-3">Verificar sua conta</h1>
                            <p className="text-muted">
                                Digite o código de 6 dígitos enviado para seu telefone
                            </p>
                        </div>

                        <form onSubmit={formik.handleSubmit}>
                            <div className="mb-4">
                                <label className="form-label fw-bold">Código de Verificação</label>
                                <InputMask 
                                    name="code"
                                    className={`form-control text-center ${formik.errors.code ? 'is-invalid' : ''}`}
                                    style={{
                                        'padding': '0.75rem',
                                        'fontSize': '1.5rem',
                                        'letterSpacing': '0.5rem',
                                        'fontWeight': 'bold'
                                    }} 
                                    mask='999999' 
                                    value={formik.values.code} 
                                    onChange={(e) => formik.setFieldValue('code', e.target.value)} 
                                    onPaste={handlePaste}
                                    placeholder="000000"
                                    maxLength={6}
                                    autoComplete="off"
                                />
                                {formik.errors.code && (
                                    <div className="invalid-feedback d-block">
                                        {formik.errors.code}
                                    </div>
                                )}
                            </div>

                            <div className="d-grid mb-4">
                                <button 
                                    type="submit" 
                                    className="btn btn-success btn-rounded"
                                    disabled={formik.isSubmitting || !formik.values.code || formik.values.code.length !== 6}
                                >
                                    {formik.isSubmitting ? 'Verificando...' : 'Verificar Código'}
                                </button>
                            </div>

                            <div className="text-center">
                                {timeLeft > 0 ? (
                                    <p className="text-muted">
                                        Reenviar código em: <strong>{formatTime(timeLeft)}</strong>
                                    </p>
                                ) : (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm text-green btn-link text-decoration-none"
                                        onClick={handleResendCode}
                                        disabled={!canResend}
                                    >
                                        Reenviar código
                                    </button>
                                )}
                            </div>
                            
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}